/**
 * DTO поиска на площадках, реестра кандидатов и предложений кандидатам
 * (сценарий заказчика Р10–Р13, ADR-0019). Контракт — `docs/kadry/openapi.json`.
 */

import type { KadryRequestStatus } from './types';

// ===== Реестр: match (Прил. Г) =====

export type KadryMatchStatus =
  | 'new'
  | 'shortlist'
  | 'invited'
  | 'interview'
  | 'offer'
  | 'hired'
  | 'rejected';

/** Пометка скоринга для HR: что уточнить или проверить. */
export type KadryScoreFlag =
  | { kind: 'permit_needs_verification'; requirement: string }
  | { kind: 'permit_expired'; requirement: string }
  | { kind: 'permit_unrecognized'; requirement: string }
  | { kind: 'experience_unknown' }
  | { kind: 'readiness_unknown'; dimension: string }
  | { kind: 'no_scorable_criteria' }
  | { kind: 'protected_requirement_ignored'; category: string };

export type KadryHardFilter =
  | { kind: 'permit_missing'; requirement: string }
  | { kind: 'readiness_refused'; dimension: string };

export interface KadryMatchView {
  id: string;
  request_id: string;
  candidate_id: string;
  profile_version: number;
  /** Балл 0–100. */
  score: number;
  scorer_version: string;
  weights_version: number;
  passed_filters: boolean;
  hard_filters: KadryHardFilter[];
  flags: KadryScoreFlag[];
  explanation_safe: string;
  /** `hh` | `internal` | … */
  source: string;
  status: KadryMatchStatus;
  status_reason: string | null;
  version: number;
}

// ===== Прогоны поиска =====

export type KadryRunTrigger = 'manual' | 'auto' | 'schedule';
export type KadryRunStatus = 'running' | 'done' | 'failed';
export type KadrySourceRunStatus = 'done' | 'skipped' | 'partial' | 'failed';

export interface KadrySourceRunReport {
  source: string | null;
  status: KadrySourceRunStatus;
  found: number;
  new_candidates: number;
  analyzed: number;
  analyzed_by_model: number;
  scored: number;
  suppressed: number;
  notes: string[];
}

export interface KadrySearchRunReport {
  sources?: KadrySourceRunReport[];
  created?: number;
  updated?: number;
  strong_new?: number;
}

export interface KadrySearchRun {
  id: string;
  request_id: string;
  profile_version: number;
  trigger: KadryRunTrigger;
  status: KadryRunStatus;
  report: KadrySearchRunReport;
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

/** Остаток пакета платных запросов API площадки. */
export interface KadryApiPackage {
  remaining: number;
  limit: number | null;
  /** До какого момента действует пакет. */
  until: string | null;
  /** Когда остаток получен с площадки. */
  checked_at: string | null;
}

/** Пакет услуги площадки (hh: пакет запросов API у каждого доступа к базе резюме — свой). */
export interface KadryQuotaPackage {
  id: string;
  name: string | null;
  limit: number;
  remaining: number;
  activated_at: string | null;
  expires_at: string | null;
  /** Когда по сверкам ОСНОВЫ остаток последний раз уменьшился — из него hh и списывает. */
  last_spent_at: string | null;
}

/** Расход запросов API hh поиском (`GET /search/cost`). */
export interface KadrySearchCost {
  /** Не больше стольких запросов за один прогон по заявке; 0 — поиск на hh не настроен. */
  hh_requests_per_run: number;
  /** Сумма по всем пакетам. */
  hh_package: KadryApiPackage | null;
  /** Пакеты по отдельности, раньше сгорающие — первыми. */
  hh_packages: KadryQuotaPackage[];
  /** Не больше стольких запросов в сутки тратит поиск по активным заявкам. */
  hh_daily_need: number;
}

/** Пакет, из которого hh списывает сейчас: тратился последним, иначе раньше сгорающий непустой. */
export function kadryCurrentPackage(packages: KadryQuotaPackage[], now = Date.now()): KadryQuotaPackage | null {
  const live = packages.filter(
    (p) => p.remaining > 0 && (!p.expires_at || Date.parse(p.expires_at) > now),
  );
  const spent = live
    .filter((p) => p.last_spent_at)
    .sort((a, b) => Date.parse(b.last_spent_at!) - Date.parse(a.last_spent_at!));
  if (spent[0]) return spent[0];
  const byExpiry = [...live].sort(
    (a, b) => (a.expires_at ? Date.parse(a.expires_at) : Infinity) - (b.expires_at ? Date.parse(b.expires_at) : Infinity),
  );
  return byExpiry[0] ?? null;
}

// ===== Предложения и ответы =====

export type KadryAnswer = 'interested' | 'not_interested';
/** `platform` — сам откликнулся на вакансию площадки (Р18). */
export type KadryResponseChannel = 'web' | 'telegram' | 'max' | 'platform';
export type KadryDeliveryStatus = 'queued' | 'sent' | 'failed' | 'unknown' | 'skipped';

export interface KadryDelivery {
  /** `hh` | `telegram` | `max`. */
  channel: string;
  /** `offer` | `followup`. */
  kind: string;
  status: KadryDeliveryStatus;
  detail: string | null;
}

export interface KadryOutreach {
  id: string;
  created_at: string;
  response: KadryAnswer | null;
  response_channel: KadryResponseChannel | null;
  responded_at: string | null;
  followup_due_at: string | null;
  followup_sent_at: string | null;
  followup_contacted: boolean | null;
  followup_answered_at: string | null;
  hr_contacted_at: string | null;
  /** Кто отметил «Связались» (ФИО); `null` при отметке — подтвердил кандидат. */
  hr_contacted_by: string | null;
  hr_not_contacted: boolean;
  /** Отправлено автоматически (автопредложение), а не кадровиком. */
  auto: boolean;
  deliveries: KadryDelivery[];
}

export interface KadryRegistryRow {
  match: KadryMatchView;
  headline: string | null;
  analyzed: boolean;
  analysis_route: string | null;
  experience_years: number | null;
  resume_url: string | null;
  full_name: string | null;
  phones: string[];
  emails: string[];
  /** Сам откликнулся на вакансию заявки: приглашать не нужно, контакты открыты. */
  applied: boolean;
  outreach: KadryOutreach | null;
  /** Переписка кадровиков о кандидате — только полному представлению. */
  comments: KadryOutreachComment[];
}

/** Комментарий кадровика к кандидату. */
export interface KadryOutreachComment {
  id: string;
  author_id: string | null;
  /** ФИО сотрудника; `null` — пользователь удалён. */
  author_name: string | null;
  text: string;
  created_at: string;
}

export interface KadryRegistrySummary {
  candidates: number;
  offers_sent: number;
  interested: number;
  not_interested: number;
  hr_not_contacted: number;
}

export interface KadryRegistry {
  summary: KadryRegistrySummary;
  rows: KadryRegistryRow[];
}

export interface KadryVacancyLink {
  source: string;
  ext_vacancy_id: string;
  url: string;
  created_at: string;
}

/** Заявка с кандидатами, ответившими «Подходит» (`GET /interested`). */
export interface KadryInterestedRequest {
  request_id: string;
  title: string;
  status: KadryRequestStatus;
  work_site_id: string | null;
  work_site_name: string | null;
  vacancies: KadryVacancyLink[];
  /** Свежие ответы сверху. */
  rows: KadryRegistryRow[];
}

// ===== Подключения: площадки и боты =====

export type KadryAccountStatus = 'not_connected' | 'connected' | 'revoked';

export interface KadrySourceAccount {
  source: string;
  status: KadryAccountStatus;
  app_configured: boolean;
  redirect_uri: string | null;
  company_name: string | null;
  ext_account_id: string | null;
  connected_at: string | null;
  discovered_at: string | null;
  last_error: string | null;
  capabilities: Record<string, unknown>;
}

export interface KadryConnectStart {
  authorize_url: string;
  redirect_uri: string;
}

export type KadryBotChannel = 'telegram' | 'max';

/** Токен задан, но бот не работает: платформа отвергла токен или нет связи. */
export type KadryBotProblem = 'token_rejected' | 'unreachable';

export interface KadryBotStatus {
  channel: KadryBotChannel;
  token_set: boolean;
  bot: { username: string; title: string | null } | null;
  problem: KadryBotProblem | null;
  link: string | null;
  enabled: boolean;
}

export type KadrySecretKind = 'api_token' | 'oauth' | 'password' | 'webhook_secret';

export interface KadrySecretMeta {
  name: string;
  kind: KadrySecretKind;
  fingerprint: string;
  version: number;
  rotated_by: string | null;
  rotated_at: string;
}

// ===== SMS (Р19) =====

/** Канал SMS на экране «Подключения». */
export interface KadrySmsStatus {
  /** Ключ SMS.ru задан на сервере. */
  configured: boolean;
  /** Имя отправителя (`SATKOM`); `null` — отправитель по умолчанию. */
  sender: string | null;
  gate_enabled: boolean;
  profile_approved: boolean;
  /** Баланс SMS.ru, ₽; `null` — не удалось узнать. */
  balance_rub: number | null;
  balance_error: string | null;
  sent_24h: number;
  daily_limit: number;
}

// ===== Профили обработки (§1.2) =====

export type KadryProcessingRecipient =
  | 'hh'
  | 'avito'
  | 'superjob'
  | 'rabota'
  | 'trudvsem'
  | 'telegram'
  | 'max'
  | 'llm_external'
  | 'smtp'
  | 'imap'
  | 'sms';

export interface KadryProcessingProfile {
  id: string;
  recipient: KadryProcessingRecipient;
  title: string;
  country: string;
  fields: Record<string, string[]>;
  legal_basis: string;
  decision_ref: string | null;
  owner: string | null;
  no_retention: boolean;
  cross_border_notice_ref: string | null;
  status: 'draft' | 'approved' | 'revoked';
  approved_by: string | null;
  approved_at: string | null;
  version: number;
  updated_at: string;
}

export interface KadryProcessingProfileInput {
  country: string;
  fields: Record<string, string[]>;
  legal_basis: string;
  decision_ref?: string | null;
  owner?: string | null;
  no_retention?: boolean;
  cross_border_notice_ref?: string | null;
}

// ===== Деньги: тарифы и бюджеты (§4.4) =====

export interface KadryTariff {
  id?: string | null;
  source: string;
  action: 'open_contacts' | 'platform_invite' | 'platform_message';
  tariff_version: string;
  rule: 'package_average' | 'actual_unit';
  package_id?: string | null;
  package_price_rub?: number | null;
  package_units?: number | null;
  unit_price_rub?: number | null;
  valid_from?: string | null;
  valid_to?: string | null;
}

export interface KadryBudget {
  id: string;
  scope: 'source_month' | 'request' | 'pool';
  scope_ref: string;
  limit_rub: number;
  reserved_rub: number;
  spent_rub: number;
  available_rub: number;
  version: number;
  updated_at: string;
}

// ===== Уведомления =====

export interface KadryNotification {
  id: string;
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string;
  ref_type: string | null;
  ref_id: string | null;
  created_at: string;
  read_at: string | null;
}
